import DOMPurify from 'dompurify';
import { postBoomiService, postSegmentService } from '../../services/trackingServices';
import { gtagEnvelope } from '../gtagEnvelope';
import { SendAsyncPostObject, SitecoreTrialContentType, FormData } from '../../types';
import { getDataResidencyParam } from '../getDataResidencyParam';
import { getCookie } from '../cookies';
import { checkUserValidity, postTeamsTrialFormDataService } from '../../services/sitecoreServices';
import { getFlowType } from '../getFlowType';
import { FORM_TYPES } from '../../consts';
import { getEventReason } from '../getExperimentVersion';
import { facebookCAPIGTMEvent } from '../facebookCAPIGTMEvent';
import { analyticsEvent } from '../analyticsEvent';

interface StandardSubmission {
  data: SitecoreTrialContentType;
  existingUserEmail: string;
  values: SendAsyncPostObject;
  marketingOptOutSeen: boolean;
  contactPermission: boolean;
}

export const handleTeamsTrialSubmission = async ({
  data,
  existingUserEmail,
  values,
  marketingOptOutSeen,
  contactPermission,
}: StandardSubmission) => {
  const { formType, trialLength, segExpQuery, segExpValue, isLoggedIn } = window.LPFormData;

  const flowType = getFlowType(formType);

  try {
    gtagEnvelope('event', 'cta_submit_click', {
      cta_name: `Start My Free ${trialLength}-Day Trial`,
      cta_url: `${window.location.origin}${window.location.pathname}${data.successUrl}`,
      cta_position: 'Trial Form',
    });

    await postSegmentService({
      event: 'teams_trial_form_click',
      properties: {
        reason: getEventReason(),
      },
    });

    const userEmail = existingUserEmail || DOMPurify.sanitize(values.email.toString().toLowerCase());

    // gather form data
    const formData: FormData = {
      email: userEmail,
      firstName: '',
      lastName: '',
      jobTitle: '',
      companyName: '',
      companySize: '',
      phoneNumber: '',
      trialSku: data.sku,
      marketingOptOutShown: marketingOptOutSeen,
      contactPermission,
      clientIp: DOMPurify.sanitize(window.akCIp),
      language: DOMPurify.sanitize(getCookie('lang')) || 'en_US',
      trialLength: trialLength || 14,
      dataResidency: getDataResidencyParam(),
      flowType,
      formType,
      affiliateClickId: DOMPurify.sanitize(getCookie('clickid')),
      segExpQuery,
      segExpValue,
    };

    if (!existingUserEmail) {
      const bossInvalid = await checkUserValidity({
        email: userEmail,
        trialSku: data.sku,
        trialLength,
      });
      if (!bossInvalid.success) {
        return { success: false, code: bossInvalid.code };
      }
    }

    const resp = await postTeamsTrialFormDataService(formData);
    if (!resp.success) {
      return { success: false, code: resp.code };
    } else {
      gtagEnvelope('event', 'trial_success_teams', {});
      facebookCAPIGTMEvent({
        contentId: 'lastpass_teams',
        email: formData.email,
      });
      await analyticsEvent(formData.email);
      if (flowType !== FORM_TYPES.PURCHASE) {
        formData.programName = data.inputs?.programName?.defaultValue;
        formData.programID = data.inputs?.programID?.defaultValue;
        await postBoomiService({
          data: formData,
          userKey: resp.successToken,
        });
        if (isLoggedIn) {
          window.location.href = data!.successLoggedInUrl;
        } else {
          window.location.href = `${data.successUrl}?email=${formData.email}`;
        }
      } else {
        window.location.href = data.successUrl;
      }
    }
    return { success: true };
  } catch (error) {
    return {
      success: false,
      code: '500',
      errorMessage: `Trial submit error: ${error.message}`,
    };
  }
};
